/**
 * Does a recent check's node count still describe this site?
 *
 * The counts in a bridge ack - "8/9 node(s) reported RSSI", "8 of 9 known
 * node(s) answered" - are quoted verbatim into Recent checks, and their
 * denominator is NOT this controller's node list. A site-wide diagnostic is
 * queued with no `router_macs`, and the bridge resolves that against its own
 * local database (`node_macs()` in xbee_control.py), which is a second list
 * that happens to agree nearly all of the time.
 *
 * It stops agreeing the moment a node is removed. The ack written before the
 * removal goes on saying nine, sits in Recent checks under today's date, and
 * gets read against the eight cards on screen with nothing to account for the
 * extra one. The same thing happens the other way while the bridge knows a node
 * this controller has not been told about yet.
 *
 * So the count is annotated rather than trusted: when it does not match what the
 * site lists now, say so. Silent when they agree, which is nearly always - a
 * note that fires on every row is one nobody reads.
 */

import type { DiagnosticResult } from '../services/espnowIrrigation';
import { countNodes, type Transport } from './nodeLabel';

/**
 * How many nodes a check covered, read off the bridge's own wording.
 *
 * Two shapes, because two bridge functions write them: the diagnostics acks use
 * "8/9 node(s) ..." and an inventory uses "8 of 9 known node(s) answered". A
 * detail with neither - a coordinator check, an ADD_NODE - returns null and is
 * left alone.
 */
const COUNT = /(\d+)\s*(?:\/|of)\s*(\d+)\s+(?:known\s+)?node/i;

export function nodesAsked(detail: string | null | undefined): number | null {
  if (!detail) return null;
  const match = COUNT.exec(detail);
  if (!match) return null;
  const asked = Number(match[2]);
  return Number.isFinite(asked) ? asked : null;
}

/**
 * One line reconciling a check's node count with the site's, or null when there
 * is nothing to say.
 *
 * @param siteNodes how many nodes this controller lists now
 */
export function coverageNote(
  result: Pick<DiagnosticResult, 'detail' | 'router_mac' | 'scope_count'>,
  siteNodes: number,
  transport: Transport,
): string | null {
  // Nothing to compare against while the node list is still loading. Firing on
  // an empty list would put the note on every row of every site for a moment.
  if (!siteNodes) return null;

  // A check that named its own targets is measured against that list, not the
  // site's: the per-node test on a node card reads "1/1" on an eight-node farm
  // and is perfectly correct. `scope_count` is absent on an older backend, where
  // `router_mac` stands in - it is set for every scoped command the UI queues.
  const scoped = result.scope_count ?? (result.router_mac ? 1 : null);
  if (scoped !== null) return null;

  const asked = nodesAsked(result.detail);
  if (asked === null || asked === siteNodes) return null;

  return `Counted ${countNodes(asked, transport)}; this site lists ${siteNodes}.`;
}
